import React, { useEffect, useState } from "react";
import { MonitorCog, Loader2, Save, KeyRound, Cpu } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const SECRETS = [
  ["emergent_llm_key", "AI key (Emergent universal key)"],
  ["jiocloud_web_api_key", "JioCloud web API key"],
  ["jiocloud_web_app_secret", "JioCloud web app secret"],
];

const SecretField = ({ k, label, info, value, onChange }) => (
  <div>
    <label className="metric-label block mb-1">{label}</label>
    <input type="password" autoComplete="off" value={value} onChange={(e) => onChange(k, e.target.value)}
      placeholder={info?.set ? `${info.masked} (saved — type to replace)` : "Not set"}
      className="studio-input !py-1.5 text-sm font-mono w-full" data-testid={`desktop-${k}-input`} />
  </div>
);

const WhisperStatus = ({ info }) => {
  const w = info?.whisper || {};
  const gpu = info?.cuda_devices > 0 && !w.force_cpu;
  return (
    <div className="flex items-start gap-2 text-xs" data-testid="desktop-whisper-status">
      <Cpu size={14} className={gpu ? "text-[#34d399]" : "text-[#94A3B8]"} />
      <span className="text-[#CBD5E1]">
        Speech transcription: {gpu ? `NVIDIA GPU · ${w.gpu_model}` : `CPU · ${w.cpu_model}`}
        {w.model && ` · loaded ${w.model} on ${w.device}`}
        {w.fallback && <span className="text-[#fbbf24]"> · {w.fallback}</span>}
      </span>
    </div>
  );
};

export const DesktopSettings = ({ info }) => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.desktopSettings().then(setS).catch(() => {}); }, []);
  if (!s) return <div className="studio-card p-5"><Loader2 className="animate-spin text-[#E6B04E]" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = Object.keys(form).length > 0;
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      setS(await api.saveDesktopSettings(body)); setForm({});
      toast.success(body.worker_concurrency ? "Saved. Worker lanes are adjusting (busy lanes finish their current job first)." : "Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="studio-card p-5 space-y-5" data-testid="desktop-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><MonitorCog size={16} className="text-[#E6B04E]" /><span className="font-head font-bold">Desktop Settings</span></div>
        <button className="btn btn-gold !py-1.5 text-xs" onClick={save} disabled={busy || !dirty} data-testid="btn-desktop-settings-save">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}Save</button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {SECRETS.map(([k, l]) => <SecretField key={k} k={k} label={l} info={s.secrets[k]} value={form[k] ?? ""} onChange={set} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="metric-label block mb-1">Worker lanes (CPU has {s.cpu_count} threads)</label>
          <input type="number" min="1" max="32" value={form.worker_concurrency ?? s.worker_concurrency}
            onChange={(e) => set("worker_concurrency", +e.target.value)} className="studio-input !py-1.5 text-sm w-full" data-testid="desktop-worker-input" />
          <div className="metric-label mt-1" style={{ fontSize: 9 }} data-testid="desktop-active-workers">{s.active_workers} running now</div>
        </div>
        <div>
          <label className="metric-label block mb-1">Working cache (GB)</label>
          <input type="number" min="1" max="2000" value={form.cache_cap_gb ?? s.cache_cap_gb}
            onChange={(e) => set("cache_cap_gb", +e.target.value)} className="studio-input !py-1.5 text-sm w-full" data-testid="desktop-cache-input" />
        </div>
      </div>
      <WhisperStatus info={info} />
      <div className="flex items-center gap-2 metric-label" style={{ fontSize: 9 }}><KeyRound size={11} />Saved to {s.settings_file}</div>
    </div>
  );
};
